// src/components/CookieConsent.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Cookie, Settings, X, Check, Info } from 'lucide-react';
import { initRedditPixel, disableRedditPixel } from '../utils/redditPixel';
import '../styles/cookie-consent.css';

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always true, cannot be disabled
    analytics: false,
    functional: false
  });
  
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      // No consent yet - show banner
      setShowBanner(true);
      // Block non-essential cookies by default
      blockAllNonEssentialCookies();
    } else {
      // Load saved preferences
      const savedPreferences = JSON.parse(consent);
      setPreferences(savedPreferences);
      applyCookieSettings(savedPreferences);
    }
  }, []);

  // CRITICAL: Block all non-essential cookies by default
  const blockAllNonEssentialCookies = () => {
    // Disable Google Analytics if it's already loaded
    if (window.gtag) {
      window['ga-disable-G-2FGR4QB793'] = true; // Replace with your GA4 ID
    }
    
    // Remove any existing analytics cookies
    document.cookie.split(";").forEach(function(c) { 
      if (c.trim().startsWith('_ga') || c.trim().startsWith('_gid') || c.trim().startsWith('_gat')) {
        document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
      }
    });
  };

  // CRITICAL: Actually apply cookie settings
  const applyCookieSettings = (prefs) => {
    // Analytics Cookies (Google Analytics)
    if (prefs.analytics) {
      enableGoogleAnalytics();
    } else {
      disableGoogleAnalytics();
    }
    
    // Functional Cookies (Trustpilot)
    if (prefs.functional) {
      console.log('✅ Functional cookies enabled');
      enableTrustpilot();
    } else {
      console.log('❌ Functional cookies disabled');
      disableTrustpilot();
    }
  };

  // Enable Google Analytics
  const enableGoogleAnalytics = () => {
      const shouldLoadGA = process.env.NODE_ENV === 'production' || 
                       process.env.REACT_APP_ENABLE_GA === 'true';

        if (!shouldLoadGA) {
    console.log('🚫 Google Analytics blocked - not in production mode');
    return;
  }

    initRedditPixel();

    const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || 'G-2FGR4QB793';
    
    // Check if already loaded
    if (window.gtag) {
      window[`ga-disable-${GA_MEASUREMENT_ID}`] = false;
      return;
    }
    
    // Load GA script dynamically
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(script);

    // Initialize GA
    script.onload = () => {
      window.dataLayer = window.dataLayer || [];
      function gtag(){window.dataLayer.push(arguments);}
      window.gtag = gtag;
      
      gtag('js', new Date());
      gtag('config', GA_MEASUREMENT_ID, {
        anonymize_ip: true, // GDPR compliance
        cookie_flags: 'SameSite=None;Secure'
      });
      
      console.log('✅ Google Analytics initialized');
    };
  };

// Disable Google Analytics
const disableGoogleAnalytics = () => {
  const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || 'G-2FGR4QB793';
  
  console.log('❌ Disabling Google Analytics...');
  
  // Disable GA tracking
  if (window.gtag) {
    window[`ga-disable-${GA_MEASUREMENT_ID}`] = true;
  }

    disableRedditPixel()
  
  // Delete existing GA cookies
  const gaCookies = ['_ga', '_gid', '_gat', '_gat_gtag_' + GA_MEASUREMENT_ID];
  gaCookies.forEach(cookieName => {
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.game2earn.co.uk`;
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  });

   const redditCookies = ['_rdt_uuid'];  // ⬅️ ADD THIS
  redditCookies.forEach(cookieName => {  // ⬅️ ADD THIS BLOCK
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.game2earn.co.uk`;
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  });
  
console.log('❌ Google Analytics and Reddit Pixel cookies deleted');
};

// Enable Trustpilot Widget
const enableTrustpilot = () => {
  // Check if already loaded
  if (window.Trustpilot || document.querySelector('script[src*="trustpilot.com"]')) {
    console.log('✅ Trustpilot already loaded');
    return;
  }
  
  // Load Trustpilot script dynamically
  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.src = '//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js';
  script.async = true;
  
  script.onload = () => {
    console.log('✅ Trustpilot widget loaded');
    // Trustpilot will auto-initialize any widgets on the page
    // No need to manually trigger loadFromElement
  };
  
  script.onerror = () => {
    console.error('❌ Failed to load Trustpilot widget');
  };
  
  document.head.appendChild(script);
};

// Disable Trustpilot Widget
const disableTrustpilot = () => {
  console.log('❌ Trustpilot widget disabled');
  
  // Remove Trustpilot script if loaded
  const trustpilotScripts = document.querySelectorAll('script[src*="trustpilot.com"]');
  trustpilotScripts.forEach(script => script.remove());
  
  // Remove Trustpilot widgets from DOM
  const trustpilotWidgets = document.querySelectorAll('.trustpilot-widget');
  trustpilotWidgets.forEach(widget => {
    widget.style.display = 'none';
  });
  
  // Clear Trustpilot global object
  if (window.Trustpilot) {
    delete window.Trustpilot;
  }
};

  const acceptAll = () => {
    const allAccepted = {
      essential: true,
      analytics: true,
      functional: true
    };
    
    setPreferences(allAccepted);
    localStorage.setItem('cookieConsent', JSON.stringify(allAccepted));
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    applyCookieSettings(allAccepted);
    setShowBanner(false);
    setShowSettings(false);
  };

  const acceptEssentialOnly = () => {
    const essentialOnly = {
      essential: true,
      analytics: false,
      functional: false
    };
    
    setPreferences(essentialOnly);
    localStorage.setItem('cookieConsent', JSON.stringify(essentialOnly));
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    applyCookieSettings(essentialOnly);
    setShowBanner(false);
    setShowSettings(false);
  };

  const saveCustomPreferences = () => {
    localStorage.setItem('cookieConsent', JSON.stringify(preferences));
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    applyCookieSettings(preferences);
    setShowBanner(false);
    setShowSettings(false);
  };

  const togglePreference = (type) => {
    if (type === 'essential') return; // Essential cookies cannot be disabled
    
    setPreferences(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="cookie-consent-overlay">
  
        {!showSettings ? (
          /* Main Cookie Banner */
          <div className="cookie-consent-banner">
            <div className="flex items-start space-x-4">
              <div className="feature-icon">
                <Cookie size={24} color="white" />
              </div>
              
              <div className="flex-1">
                <h3 className="text-xl text-primary font-semibold mb-3">
                  We Use Cookies 🍪
                </h3>
                
                <p className="text-secondary text-sm mb-4 leading-relaxed">
                  We use essential cookies for login and security. Optional analytics cookies 
                  (Google Analytics) help us improve your experience. We respect your privacy - 
                  you're in control.
                </p>
                
                <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
                  <button
                    onClick={acceptAll}
                    className="cookie-btn cookie-btn-primary flex-1"
                  >
                    <Check size={16} />
                    Accept All Cookies
                  </button>
                  
                  <button
                    onClick={acceptEssentialOnly}
                    className="cookie-btn cookie-btn-secondary flex-1"
                  >
                    Essential Only
                  </button>
                  
                  <button
                    onClick={() => setShowSettings(true)}
                    className="cookie-btn cookie-btn-secondary"
                  >
                    <Settings size={16} />
                    Customize
                  </button>
                </div>
                
                <div className="mt-3 text-center">
                  <button
                    onClick={() => navigate('/cookie-policy')}
                    className="text-cyan text-xs hover:underline"
                  >
                    Learn more about our Cookie Policy
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Cookie Settings Panel */
          <div className="cookie-consent-settings">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-2">
                  <Settings className="text-purple" size={24} />
                  <h3 className="text-xl text-primary font-semibold">
                    Cookie Preferences
                  </h3>
                </div>
                <button
                  onClick={() => setShowSettings(false)}
                  className="text-gray-400 hover:text-white"
                >
                  <X size={20} />
                </button>
              </div>
              
              <p className="text-secondary text-sm mb-6">
                Choose which types of cookies you'd like to allow. Essential cookies are 
                required for login, security, and core functionality.
              </p>
              
              <div className="space-y-4">
                {/* Essential Cookies */}
                <div className="bg-gray-800 p-4 rounded-lg border-l-4 border-green-500">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-success rounded-full"></div>
                      <h4 className="text-primary font-semibold">Essential Cookies</h4>
                    </div>
                    <div className="bg-success text-white px-2 py-1 rounded text-xs font-semibold">
                      Always Active
                    </div>
                  </div>
                  <p className="text-secondary text-sm">
                    Firebase Authentication, security, and session management. Required for 
                    the platform to function. Cannot be disabled.
                  </p>
                </div>
                
                {/* Analytics Cookies */}
                <div className={`bg-gray-800 p-4 rounded-lg border-l-4 ${preferences.analytics ? 'border-cyan' : 'border-gray-600'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => togglePreference('analytics')}
                        className={`cookie-toggle ${preferences.analytics ? 'cookie-toggle-active bg-cyan' : 'bg-gray-600'}`}
                      >
                        <div className="cookie-toggle-thumb" />
                      </button>
                      <h4 className="text-primary font-semibold">Analytics Cookies</h4>
                    </div>
                    <span className="text-xs text-gray-400">Google Analytics</span>
                  </div>
                  <p className="text-secondary text-sm">
                    Help us understand how you use our platform to improve performance 
                    and user experience. We use Google Analytics with IP anonymization.
                  </p>
                </div>
                
                {/* Functional Cookies */}
                <div className={`bg-gray-800 p-4 rounded-lg border-l-4 ${preferences.functional ? 'border-warning' : 'border-gray-600'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => togglePreference('functional')}
                        className={`cookie-toggle ${preferences.functional ? 'cookie-toggle-active bg-warning' : 'bg-gray-600'}`}
                      >
                        <div className="cookie-toggle-thumb" />
                      </button>
                      <h4 className="text-primary font-semibold">Functional Cookies</h4>
                    </div>
                    <span className="text-xs text-gray-400">Trustpilot Reviews</span>
                  </div>
                  <p className="text-secondary text-sm">
                    Remember your preferences and load third-party review widgets (Trustpilot) 
                    for social proof. Helps provide a personalized experience.
                  </p>
                </div>
              </div>
              
              <div className="mt-6 flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
                <button
                  onClick={saveCustomPreferences}
                  className="cookie-btn cookie-btn-primary flex-1"
                >
                  <Check size={16} />
                  Save My Preferences
                </button>
                
                <button
                  onClick={acceptAll}
                  className="cookie-btn cookie-btn-secondary flex-1"
                >
                  Accept All
                </button>
              </div>
              
              <div className="mt-4 p-3 bg-blue-900/20 border border-blue-500/30 rounded-lg">
                <div className="flex items-center space-x-2">
                  <Info className="text-blue-300" size={16} />
                  <p className="text-blue-300 text-xs">
                    Changes take effect immediately. You can update these preferences anytime 
                    via our Cookie Policy page or by clearing your browser data.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default CookieConsent;